import { Box, Grid, Tab } from "@mzc-pdc/ui";
import { StyledTabs } from "./tabs.styled";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";
import { forwardRef, memo, useCallback, useMemo } from "react";

export const TabPanel = memo((props) => {
    const { children, value, index, sx, ...other } = props;
    return (
        <Box role="tabpanel" key={`tab-panel-${index}`} hidden={value !== index} sx={{ ...sx }} {...other}>
            {children}
        </Box>
    );
});

const TabLabel = memo(({ label, count, icon, iconPosition }) => (
    <Grid container flexDirection={"row"} alignItems={"center"}>
        {label}
        {count !== undefined && (
            <Grid ml={1}>
                <Typography variant={"body1_700"} color={theme.palette.primary[800]} children={count} />
            </Grid>
        )}
        {icon && <Grid ml={1}>{icon}</Grid>}
    </Grid>
));

export const Tabs = memo(
    forwardRef(
        (
            {
                items,
                value,
                type,
                orientation,
                textColor = "secondary",
                indicatorColor = "secondary",
                indicatorPosition = "bottom",
                TabIndicatorProps,
                isBordered = true,
                variant,
                onChangeTab,
                scrollButtons,
                scrollButtonPosition,
                setRef,
                actionButton,
                divider,
                sx,
            },
            ref,
        ) => {
            const { t } = useTranslation();

            const handleChange = useCallback(
                (e, v) => {
                    onChangeTab(v);
                },
                [onChangeTab],
            );

            const tabItems = useMemo(
                () =>
                    items?.map((item, index) => (
                        <Tab
                            key={item.value || index}
                            iconPosition={item.iconPosition}
                            label={
                                <TabLabel
                                    label={item.label}
                                    count={item.count}
                                    icon={item.icon}
                                    iconPosition={item.iconPosition}
                                />
                            }
                            value={item.value}
                            disabled={item.disabled}
                            ref={(ref) => setRef && setRef(ref, item)}
                        />
                    )),
                [items],
            );

            return (
                <StyledTabs
                    ref={ref}
                    value={value}
                    onChange={handleChange}
                    orientation={orientation}
                    type={type}
                    divider={divider}
                    textColor={textColor}
                    indicatorColor={indicatorColor}
                    indicatorPosition={indicatorPosition}
                    TabIndicatorProps={TabIndicatorProps}
                    isBordered={isBordered}
                    variant={variant}
                    scrollButtons={scrollButtons}
                    scrollButtonPosition={scrollButtonPosition}
                    sx={sx}
                >
                    {tabItems}
                    {actionButton}
                </StyledTabs>
            );
        },
    ),
);
